import { faMinus } from "@jewlr/pro-regular-svg-icons/faMinus"
import { faPlus } from "@jewlr/pro-regular-svg-icons/faPlus"
import { faTimesCircle } from "@jewlr/pro-regular-svg-icons/faTimesCircle"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import { connect } from "react-redux"

import { applyCoupon, removeCoupon } from "areas/cart/store/actions"
import Button from "components/ui/button"
import Expand from "components/ui/expand"
import FloatInput from "components/ui/float-input"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { isTestMode } from "helpers/application"
import { freeGiftPromoEnabled } from "helpers/holiday"
import { cn } from "styles/utils"

const InternalPromoCode = ({
  applyCoupon,
  couponApplied,
  couponDescription,
  couponRemove,
  modal,
  promo,
}) => {
  const promoInputRef = useRef()
  const [couponValue, setCouponValue] = useState(couponApplied || "")
  const [applying, setApplying] = useState(false)
  const [couponError, setCouponError] = useState("")
  const [showPromoCode, togglePromoCode] = useState(!!couponApplied)

  const handleApply = () => {
    setCouponError("")
    !applying &&
      applyCoupon(
        couponValue,
        promoInputRef.current,
        setApplying,
        setCouponError
      )
    setApplying(true)
  }

  const freeGiftPromoApplied = () =>
    freeGiftPromoEnabled() &&
    couponApplied?.toUpperCase() ===
      sl.config.free_gift_promo?.code?.toUpperCase()

  const handleTogglePromoCode = useCallback(() => {
    if (!isTestMode) {
      dataLayer.push({
        event: "toggle-promo-code-accordion",
        source: modal ? "atc-modal" : "cart",
      })
    }
    togglePromoCode((prev) => !prev)
  }, [])

  useEffect(() => {
    if (couponApplied) {
      togglePromoCode(true)
    }
  }, [couponApplied])

  return (
    <section
      aria-labelledby="cart-promo-code-heading"
      className={cn(
        modal
          ? "py-8"
          : "border-y-grey-30 tablet:border-none tablet:p-0 border-y py-16",
        promo && "border-grey-20 rounded-md border bg-white px-12 py-16"
      )}
    >
      <h2 id="cart-promo-code-heading">
        <Button
          aria-controls="cart-promo-code-panel"
          aria-expanded={showPromoCode}
          className="w-full justify-between"
          data-cy="cart-promo-code-toggle"
          onClick={handleTogglePromoCode}
          text
          underline={modal}
          variant="primary"
        >
          <p
            className={cn(
              "text-14 leading-20",
              modal ? "font-semibold" : "font-medium"
            )}
          >
            Add Promo Code
          </p>
          <FontAwesomeIcon
            className="text-12"
            icon={showPromoCode ? faMinus : faPlus}
          />
        </Button>
      </h2>
      <Expand id="cart-promo-code-panel" show={showPromoCode}>
        {/* Applied promo codes */}
        <div className={cn(modal ? "mt-8" : "mb-12 mt-24")}>
          {couponApplied ? (
            <>
              <div className="flex items-center justify-between gap-16">
                <p className="text-14 inline-flex flex-1 items-center gap-4 rounded-md bg-[#0088000d] p-8">
                  <span className="font-bold">{couponApplied}</span> &ndash;
                  Code Applied
                </p>
                <Button
                  aria-label="Remove applied promo code"
                  data-cy="cart-promo-code-remove"
                  onClick={couponRemove}
                  text
                  variant="secondary"
                >
                  <FontAwesomeIcon className="text-16" icon={faTimesCircle} />
                </Button>
              </div>
              {couponDescription && (
                <p
                  className={cn(
                    "text-grey-65 text-12 leading-12 mt-6",
                    freeGiftPromoApplied() &&
                      "text-green-darker text-14 font-bold"
                  )}
                >
                  {couponDescription}
                </p>
              )}
            </>
          ) : (
            <form>
              {/* Apply promo codes */}
              <div className="flex w-full justify-between gap-12">
                <FloatInput
                  autoComplete="off"
                  className={cn(modal && "text-14 h-36")}
                  containerClassName="flex-1 max-w-256"
                  data-cy="cart-promo-code-input"
                  id="promo-code"
                  label="Enter Promo Code"
                  name="promo-code"
                  onChange={(e) => setCouponValue(e.target.value)}
                  onReturn={handleApply}
                  ref={promoInputRef}
                  value={couponValue}
                  variant="checkout"
                />
                <Button
                  className={cn(
                    modal ? "text-14 w-128" : "h-44 w-72 rounded-md"
                  )}
                  data-cy="cart-promo-code-apply-btn"
                  disabled={!couponValue}
                  loading={applying}
                  onClick={handleApply}
                  size={modal ? "sm" : "lg"}
                  variant={modal ? "primary" : "secondary"}
                >
                  {!applying && <span>Apply</span>}
                </Button>
              </div>
              {couponError && (
                <p className="text-error text-12 leading-12 mt-6">
                  {couponError}
                </p>
              )}
            </form>
          )}
        </div>
      </Expand>
    </section>
  )
}

InternalPromoCode.propTypes = {
  applyCoupon: PropTypes.func,
  couponApplied: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  couponDescription: PropTypes.string,
  couponRemove: PropTypes.func,
  modal: PropTypes.bool,
  promo: PropTypes.bool,
}

const mapStateToProps = (state) => {
  return {
    couponApplied: state.cart.summary?.promo_code?.promo_code_applied,
    couponDescription:
      state.cart.summary?.promo_code?.promo_code_applied_description,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    applyCoupon: (value, promoInputRef, setApplying, setCouponError) => {
      dispatch(applyCoupon(value, promoInputRef, setApplying, setCouponError))
    },
    couponRemove: () => {
      dispatch(removeCoupon())
    },
  }
}

const PromoCode = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalPromoCode)

export default PromoCode
